import { ROUTES } from '@/constants/routes';

export const LINKEDIN_INSIGHT_PARTNER_ID = '1761698';
export const LINKEDIN_INSIGHT_SCRIPT_URL =
  'https://snap.licdn.com/li.lms-analytics/insight.min.js';

const LINKEDIN_INSIGHT_SCRIPT_ID = 'linkedin-insight-tag';
const LINKEDIN_INSIGHT_PRODUCTION_HOSTS = new Set(['agentous.ai']);

type LinkedInTrackArgs = [string, Record<string, unknown>?];

interface LinkedInTrackFunction {
  (...args: LinkedInTrackArgs): void;
  q?: LinkedInTrackArgs[];
}

interface LinkedInInsightWindow extends Window {
  _linkedin_partner_id?: string;
  _linkedin_data_partner_ids?: string[];
  lintrk?: LinkedInTrackFunction;
}

interface LoadLinkedInInsightTagOptions {
  currentDocument?: Document;
  currentLocation?: Pick<Location, 'hostname' | 'pathname'>;
  currentWindow?: LinkedInInsightWindow;
}

function normalizePathname(pathname: string) {
  return pathname.replace(/\/+$/, '') || '/';
}

export function shouldLoadLinkedInInsightTag(
  currentLocation: Pick<Location, 'hostname' | 'pathname'>
) {
  return (
    LINKEDIN_INSIGHT_PRODUCTION_HOSTS.has(
      currentLocation.hostname.toLowerCase()
    ) && normalizePathname(currentLocation.pathname) === ROUTES.MEET_LIVE_AI
  );
}

export function loadLinkedInInsightTag({
  currentDocument = typeof document === 'undefined' ? undefined : document,
  currentLocation = typeof window === 'undefined' ? undefined : window.location,
  currentWindow = typeof window === 'undefined'
    ? undefined
    : (window as LinkedInInsightWindow),
}: LoadLinkedInInsightTagOptions = {}) {
  if (!currentDocument || !currentLocation || !currentWindow) return false;
  if (!shouldLoadLinkedInInsightTag(currentLocation)) return false;

  currentWindow._linkedin_partner_id = LINKEDIN_INSIGHT_PARTNER_ID;
  currentWindow._linkedin_data_partner_ids =
    currentWindow._linkedin_data_partner_ids || [];

  if (
    !currentWindow._linkedin_data_partner_ids.includes(
      LINKEDIN_INSIGHT_PARTNER_ID
    )
  ) {
    currentWindow._linkedin_data_partner_ids.push(LINKEDIN_INSIGHT_PARTNER_ID);
  }

  if (!currentWindow.lintrk) {
    const queuedLintrk: LinkedInTrackFunction = (...args) => {
      queuedLintrk.q?.push(args);
    };
    queuedLintrk.q = [];
    currentWindow.lintrk = queuedLintrk;
  }

  if (currentDocument.getElementById(LINKEDIN_INSIGHT_SCRIPT_ID)) return true;

  const script = currentDocument.createElement('script');
  script.id = LINKEDIN_INSIGHT_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.src = LINKEDIN_INSIGHT_SCRIPT_URL;

  const firstScript = currentDocument.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    currentDocument.head.append(script);
  }

  return true;
}
